import { useState, type FormEvent } from 'react';
import { trpc } from '../../lib/trpc';
import { useAsync } from '../../lib/useAsync';
import { Loading, ErrorBox, EmptyBox, PageTitle } from '../../components/admin/ui';
import { mesLabel } from '../../lib/format';

/**
 * Console › Índice INCC. O índice agora é buscado AUTOMATICAMENTE do SGS/Banco
 * Central (INCC-M, série 7456) por um job mensal — esta tela virou
 * MONITORAMENTO: acompanhar a série e, em caráter EXCEPCIONAL, forçar uma busca
 * ("Buscar agora") ou corrigir/lançar um mês na mão (se a fonte trouxer valor
 * errado). O lançamento manual não é mais o fluxo normal.
 */
const ANO_MES = /^\d{4}-(0[1-9]|1[0-2])$/;

function fmtPct(v: string | number): string {
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${n.toFixed(2).replace('.', ',')}%`;
}

/** true quando o mês foi gravado pelo job automático (criado_por = auto:...). */
function ehAutomatico(criadoPor: string | null | undefined): boolean {
  return typeof criadoPor === 'string' && criadoPor.startsWith('auto:');
}

export function AdminIndices() {
  const { data, loading, error, reload } = useAsync(() => trpc.admin.indices.list.query());
  const [anoMes, setAnoMes] = useState('');
  const [pct, setPct] = useState('');
  const [saving, setSaving] = useState(false);
  const [formErro, setFormErro] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [buscaMsg, setBuscaMsg] = useState<string | null>(null);

  async function buscarAgora() {
    setBuscando(true);
    setBuscaMsg(null);
    try {
      const r = await trpc.admin.indices.buscarAgora.mutate();
      setBuscaMsg(
        r.inseridos.length > 0
          ? `Busca concluída: ${r.inseridos.length} ${r.inseridos.length === 1 ? 'mês novo gravado' : 'meses novos gravados'} (${r.inseridos.join(', ')}).`
          : `Busca concluída: nada novo; a série já estava em dia (mês esperado ${r.mesEsperado}${r.mesEsperadoPresente ? ' presente' : ' ainda não publicado pela fonte'}).`,
      );
      reload();
    } catch (err) {
      setBuscaMsg(`Falha ao buscar do Banco Central: ${(err as Error).message ?? 'erro'}.`);
    } finally {
      setBuscando(false);
    }
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setFormErro(null);
    const variacao = Number(pct.replace(',', '.'));
    if (!ANO_MES.test(anoMes)) return setFormErro('Selecione um mês válido (AAAA-MM).');
    if (!Number.isFinite(variacao)) return setFormErro('Informe a variação como número.');
    if (variacao < -5 || variacao > 15) return setFormErro('A variação deve ficar entre -5% e +15%.');
    setSaving(true);
    try {
      await trpc.admin.indices.upsert.mutate({ anoMes, variacaoPercentual: variacao });
      setAnoMes('');
      setPct('');
      reload();
    } catch (err) {
      setFormErro((err as Error).message ?? 'Falha ao salvar.');
    } finally {
      setSaving(false);
    }
  }

  async function excluir(am: string) {
    await trpc.admin.indices.remove.mutate({ anoMes: am });
    reload();
  }

  return (
    <div className="mx-auto max-w-3xl" data-testid="indices-page">
      <PageTitle titulo="Índice INCC" />

      {/* Monitoramento: origem automática + buscar agora */}
      <div className="card p-5">
        <div className="text-[15px] font-semibold text-ink">Atualização automática</div>
        <div className="mt-0.5 text-xs text-ink-3">
          O INCC-M é buscado sozinho do Banco Central (SGS, série 7456) todo mês. Esta tela é para
          acompanhar. Se a fonte atrasar, você pode forçar a busca; correção manual só em caso excepcional.
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={buscarAgora}
            disabled={buscando}
            className="h-10 rounded-[10px] px-4 text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: 'var(--accent)' }}
            data-testid="incc-buscar-agora"
          >
            {buscando ? 'Buscando…' : 'Buscar agora do Banco Central'}
          </button>
          {buscaMsg && <span className="text-xs text-ink-2">{buscaMsg}</span>}
        </div>
      </div>

      {/* Correção manual (excepcional) */}
      <form onSubmit={salvar} className="card mt-4 p-5">
        <div className="text-[15px] font-semibold text-ink">Correção manual (excepcional)</div>
        <div className="mt-0.5 text-xs text-ink-3">
          Use só se a fonte trouxer um valor errado. Um número por mês; reaplicar o mesmo mês atualiza o valor.
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            Mês
            <input
              type="month"
              value={anoMes}
              onChange={(e) => setAnoMes(e.target.value)}
              className="h-10 rounded-[10px] border border-line bg-surface px-3 text-sm text-ink"
              data-testid="incc-mes"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            Variação (%)
            <input
              type="number"
              step="0.001"
              min={-5}
              max={15}
              placeholder="ex.: 0,45"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              className="h-10 w-32 rounded-[10px] border border-line bg-surface px-3 text-sm text-ink"
              data-testid="incc-pct"
            />
          </label>
          <button
            type="submit"
            disabled={saving}
            className="h-10 rounded-[10px] border px-4 text-sm font-semibold disabled:opacity-60"
            style={{ borderColor: 'var(--line-2)', color: 'var(--ink)' }}
            data-testid="incc-salvar"
          >
            {saving ? 'Salvando…' : 'Corrigir mês'}
          </button>
        </div>
        {formErro && (
          <div className="mt-3 rounded-[10px] px-3 py-2 text-sm" style={{ background: 'var(--risk-soft)', color: 'var(--risk)' }}>
            {formErro}
          </div>
        )}
      </form>

      {/* Série lançada */}
      <div className="mt-4">
        {loading && <Loading />}
        {error && <ErrorBox msg={error} onRetry={reload} />}
        {data && data.length === 0 && (
          <EmptyBox titulo="Nenhum índice ainda" descricao="Clique em “Buscar agora” para trazer a série do Banco Central." />
        )}
        {data && data.length > 0 && (
          <div className="card overflow-hidden">
            <div className="border-b border-line px-5 py-3 text-[15px] font-semibold text-ink">
              Série INCC ({data.length} {data.length === 1 ? 'mês' : 'meses'})
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs text-ink-3">
                  <th className="px-5 py-2 font-medium">Mês</th>
                  <th className="px-3 py-2 font-medium">Variação</th>
                  <th className="px-3 py-2 font-medium">Origem</th>
                  <th className="px-5 py-2 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.map((i) => (
                  <tr key={i.anoMes} data-testid="incc-linha">
                    <td className="px-5 py-2.5 text-ink">{mesLabel(i.anoMes)}</td>
                    <td className="num px-3 py-2.5" style={{ color: Number(i.variacaoPercentual) < 0 ? 'var(--risk)' : 'var(--ink)' }}>
                      {fmtPct(i.variacaoPercentual)}
                    </td>
                    <td className="px-3 py-2.5">
                      {ehAutomatico(i.criadoPor) ? (
                        <span
                          className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                          style={{ color: 'var(--accent)', background: 'var(--accent-soft)' }}
                          data-testid="incc-badge-auto"
                        >
                          Automático (SGS)
                        </span>
                      ) : (
                        <span className="text-ink-4">{i.criadoPor ?? 'manual'}</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setAnoMes(i.anoMes);
                          setPct(String(Number(i.variacaoPercentual)));
                        }}
                        className="mr-3 text-xs font-medium text-accent hover:underline"
                      >
                        Corrigir
                      </button>
                      <button
                        type="button"
                        onClick={() => excluir(i.anoMes)}
                        className="text-xs font-medium hover:underline"
                        style={{ color: 'var(--risk)' }}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
